'use strict';

import { t } from 'i18next';
import PubSub from 'pubsub-js';
import * as THREE from 'three';
import { Object3D, Vector3 } from 'three';

import {
  ContextMenuItem,
  SkyViewContextMenuProps,
} from '../../react/components/skyview/context-menu';
import { canAssetBeTransformedWithReferencePointTool } from '../../react/components/skyview/right-menu/tools/reference-point-transform/reference-point-transform';
import { SkyviewAssetPositionProps } from '../../react/components/skyview/tooltip/skyview-asset-position-tooltip';
import { userStateAdapter } from '../../react/state/user-state/user-state-adapter';
import { ThemeManager } from '../../react/themes/theme-manager';
import { MetaDefinition } from '../../services/metadata/meta-types';
import { FeatureFlagsManager } from '../../utils/core/feature-flags-manager';
import { permissionChecks } from '../../utils/permission-check';
import { isShownBelow } from '../../utils/three/visibility';
import { isDefined } from '../../utils/variables';
import { AssetManager } from '../asset-manager';
import cameraManager from '../camera-manager';
import { SkyviewCamera } from '../cameras/types';
import { CoordinateManager } from '../coordinate-manager';
import { Design } from '../elements/design';
import { Conic } from '../elements/design/design-node/utils/conic';
import { Dxf } from '../elements/dxf';
import { Geometry } from '../elements/geometry';
import { Glb } from '../elements/glb';
import { getIfcNode } from '../elements/glb-layers';
import { Model } from '../elements/model';
import { TerrainModel } from '../elements/terrain-model';
import { InputHandler } from '../input-handler';
import { publish, SubscriptionTopic } from '../messaging/pubsub';
import {
  MouseButtonType,
  MouseInputData,
  TouchInputData,
} from '../utilities/input-event/input-event-data-extractor';
import { clamp } from '../utilities/math-helpers';
import { Pick, setRayCasterParamsBasedOnCamera } from '../utilities/pick';
import { ViewSettings } from '../view-settings';
import { Tool } from './tool';
import { buildTooltipProps, MenuTooltipTool, showToolTooltip } from './tooltip-helper';
import { AssetSnapping } from './utils/asset-snapping';
import { deselectAsset, editAsset, toggleAsset } from './utils/context-menu/commands';
import { HeightLayersGuide } from './utils/height-layers';
import { CenterCurveHighlight } from './utils/snap-candidates';

enum SelectToolState {
  Selecting,
  Editing,
}

export const excludeAsset =
  (assetToExclude: Object3D, predicate: (asset: Object3D) => boolean = () => true) =>
  (asset: Object3D) =>
    assetToExclude !== asset && predicate(asset);

export const isAssetPickable = (asset: Object3D) => {
  return (
    asset.visible &&
    (asset instanceof Dxf ||
      asset instanceof Glb ||
      asset instanceof TerrainModel ||
      asset instanceof Model ||
      asset instanceof Geometry ||
      asset instanceof Design)
  );
};

export class SelectTool extends Tool {
  assetMeasuring = false;
  private _snapping?: AssetSnapping;
  /** Whether _snapping was made for the CAD viewer, see snapping. */
  private snappingForCadViewer?: boolean;
  private centerHighlight = new CenterCurveHighlight();
  /**
   * All heights at the snapped point, while its option is on.
   */
  readonly heightsGuide = new HeightLayersGuide();

  contextMenuCallback?: (contextMenu?: SkyViewContextMenuProps) => void = undefined;
  tooltipCallback?: (tooltip?: SkyviewAssetPositionProps) => void = undefined;
  /**
   * Set while the tooltip is shown in the snapping dialog, which then gets it instead of the cursor.
   */
  onMenuTooltipChanged?: MenuTooltipTool['onMenuTooltipChanged'];

  private state: SelectToolState = SelectToolState.Selecting;

  private editPointIdx: number;
  private lastValidPoint: THREE.Vector3 | undefined;

  private raycaster: THREE.Raycaster;

  private xCoord = 0;
  private yCoord = 0;
  private zCoord = 0;

  private canvasAssetInfoBox: HTMLDivElement | undefined;

  private editingGhostPoint: boolean;

  /** The object of an IFC model that is highlighted as the mouse is over it, see hoverIfcObject. */
  private hoveredIfc?: IfcPick;
  /** Where the mouse is, for the object of an IFC model below it to be found once a frame. */
  private ifcHoverPosition?: { x: number; y: number };

  constructor(inputHandler: InputHandler) {
    super('select', inputHandler);

    this.visible = false;
    this.editPointIdx = -1;

    this.editingGhostPoint = false;

    this.raycaster = new THREE.Raycaster();
    this.raycaster.params.Points = { threshold: 0.2 };

    this.add(this.centerHighlight);
    this.add(this.heightsGuide);
  }

  onTooltipChanged(callback: (tooltip?: SkyviewAssetPositionProps) => void) {
    this.tooltipCallback = callback;
  }

  onContextMenuChanged(callback: (contextMenu?: SkyViewContextMenuProps) => void) {
    this.contextMenuCallback = callback;
  }

  getMeta(): MetaDefinition {
    const selected = AssetManager.instance.getSelected();
    return (selected as any).getMeta?.();
  }

  showContextMenu(e: MouseInputData) {
    if (this.state === SelectToolState.Editing) {
      return false;
    }

    // Geometry vertex deletion is now handled in updateContextMenu() via the React
    // context menu (contextMenuCallback). The old PubSub path (skyviewContextMenu.UpdateItems)
    // has no subscriber in the React-based viewer page.
    return false;
  }

  getContextMenuItems(e: MouseInputData) {
    const selected = AssetManager.instance.getSelected();
    const camera = cameraManager().getActiveCamera();

    if (camera && selected instanceof Geometry) {
      return selected.getContextMenuItems(e.pointer.position.x, e.pointer.position.y, camera);
    }

    return [];
  }

  onMouseDown(e: MouseInputData) {
    this.mousePosition = e.pointer.client;

    return true;
  }

  async onMouseUp(e: MouseInputData) {
    const camera = cameraManager().getActiveCamera();
    if (!this.withinThreshold(e.pointer.client.x, e.pointer.client.y) || !camera) {
      return Promise.resolve(true);
    }

    if (this.state === SelectToolState.Editing) {
      this.assetMeasuring = false;
      this.showCenterCurves(undefined);
      this.heightsGuide.hide();
    }

    const selected = AssetManager.instance.getSelected();

    if (e.button.type === MouseButtonType.RightButton) {
      if (this.state === SelectToolState.Editing) {
        if (this.editingGhostPoint) {
          (selected as any)?.removePoint?.(this.editPointIdx);
        } else if (selected) {
          if (selected) {
            (selected as any)?.resetEditing?.(this.editPointIdx);
          }
          PubSub.publish('newSelection', selected);
        }

        this.state = SelectToolState.Selecting;

        this.clearTooltip();
      } else {
        const executed = this.updateContextMenu(e);

        if (!executed) {
          this.disable();
        }
      }
    } else {
      if (this.state === SelectToolState.Editing) {
        await selected?.onChange();
        this.state = SelectToolState.Selecting;

        this.clearTooltip();
        return Promise.resolve(true);
      }

      // Check if AssetManager.instance.getSelected()
      // If so, then pick against its selectables first..
      // If no hits, then do dynamicAssets.pick

      if (selected) {
        this.editPointIdx = (selected as any).pick?.(
          e.pointer.position.x,
          e.pointer.position.y,
          camera,
        );
        this.state = this.editPointIdx > -1 ? SelectToolState.Editing : SelectToolState.Selecting;
        if (this.state === SelectToolState.Editing) {
          if (selected instanceof Geometry) {
            const newPointIndex = selected.convertGhostSphere(this.editPointIdx);
            this.editingGhostPoint = this.editPointIdx !== newPointIndex;
            this.editPointIdx = newPointIndex;
          }

          const point = (selected as any).getPointWorldPosition(this.editPointIdx);
          const projectionorigo = CoordinateManager.getInstance().getProjectionOrigo()!;

          // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
          this.xCoord = point.x + (projectionorigo.x || 0);
          // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
          this.yCoord = point.y + (projectionorigo.y || 0);
          // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
          this.zCoord = point.z + (projectionorigo.z || 0);
          this.lastValidPoint = point;

          this.updateTooltip();

          return Promise.resolve(true);
        }
      }

      const asset = AssetManager.instance.pick(
        e.pointer.position.x,
        e.pointer.position.y,
        camera.getTHREECamera(),
      );
      AssetManager.instance.select(asset);

      if (!AssetManager.instance.getSelected()) {
        this.disable();
      }
    }

    return Promise.resolve(true);
  }

  onTouchStart(e: TouchInputData) {
    if (e.changedTouches.length === 1) {
      this.mousePosition = e.changedTouches[0].client;
    }

    return true;
  }

  onTouchEnd(e: TouchInputData) {
    if (e.changedTouches.length !== 1) {
      return true;
    }

    // Raycast mot dynamicAssets
    if (this.withinThreshold(e.changedTouches[0].client.x, e.changedTouches[0].client.y)) {
      const camera = cameraManager().getActiveCamera();

      if (camera) {
        const asset = AssetManager.instance.pick(
          e.changedTouches[0].position.x,
          e.changedTouches[0].position.y,
          camera.getTHREECamera(),
        );
        AssetManager.instance.select(asset);
      }
    }

    return true;
  }

  onMouseMove(e: MouseInputData) {
    this.clearContextMenu();
    const camera = cameraManager().getActiveCamera();
    const selected = AssetManager.instance.getSelected();
    let pickedPoint: Vector3 | undefined;
    if (camera && this.state === SelectToolState.Editing) {
      if (
        selected instanceof Geometry &&
        selected.snap(this.editPointIdx, e.pointer.client.x, e.pointer.client.y, camera)
      ) {
        this.showCenterCurves(undefined);
        this.heightsGuide.hide();
        this.updateTooltip();
        return true;
      }

      if (this.snappingOptions) {
        pickedPoint = this.findSnapPoint(camera, e, selected);
      } else if (this.assetMeasuring || ViewSettings.instance.isCadViewer) {
        const filter = isDefined(selected)
          ? excludeAsset(selected, isAssetPickable)
          : isAssetPickable;
        pickedPoint = Pick.instance.assetPoint(e.pointer.position.x, e.pointer.position.y, filter, {
          pixelSortScreenPosition: { client: e.pointer.client },
        });
      } else {
        pickedPoint = Pick.instance.pointcloudPoint(e.pointer.position.x, e.pointer.position.y);
      }

      (selected as any)?.recalculate?.(this.editPointIdx, pickedPoint);
      this.heightsGuide.update(
        camera,
        pickedPoint,
        selected && excludeAsset(selected, isAssetPickable),
      );

      if (pickedPoint) {
        const projectionorigo = CoordinateManager.getInstance().getProjectionOrigo()!;

        this.xCoord = pickedPoint.x + (projectionorigo.x || 0);
        this.yCoord = pickedPoint.y + (projectionorigo.y || 0);
        this.zCoord = pickedPoint.z + (projectionorigo.z || 0);

        this.lastValidPoint = pickedPoint;
      }

      this.updateTooltip();
    } else if (selected) {
      // something is selected but we are not editing it...
      // Check if mouse over an

      const idx = (selected as any)?.pick?.(e.pointer.position.x, e.pointer.position.y, camera);
      (selected as any)?.mouseOver?.(idx);
    }

    // Behind a flag until it is decided whether it is wanted.
    if (this.state === SelectToolState.Editing || !this.ifcHoverHighlight) {
      this.hoverIfcObject(undefined);
    } else {
      this.scheduleIfcHover(e.pointer.position.x, e.pointer.position.y);
    }

    return true;
  }

  /**
   * Whether the object of the selected IFC model below the mouse is highlighted, see hoverIfcObject.
   */
  get ifcHoverHighlight() {
    return FeatureFlagsManager.instance.isEnabled('SK-7538-ifc-hover-highlight');
  }

  get snappingOptions() {
    return FeatureFlagsManager.instance.isEnabled('SK-7558-measure-snapping-options');
  }

  /**
   * The snapping of the point being moved, set in the snapping dialog, which replaces
   * assetMeasuring with the flag on. Like before, a CAD drawing is snapped to in the CAD viewer.
   * The tool outlives SkyView, so a project opened in or out of the CAD viewer starts over from
   * its default.
   */
  get snapping() {
    const cadViewer = ViewSettings.instance.isCadViewer;
    if (!this._snapping || this.snappingForCadViewer !== cadViewer) {
      this._snapping = new AssetSnapping({ tiledModel: false, assets: cadViewer });
      this.snappingForCadViewer = cadViewer;
    }
    return this._snapping;
  }

  /**
   * Finds where the point being moved snaps to, not to the asset itself, and highlights the curves
   * whose center it snaps to.
   */
  private findSnapPoint(
    camera: SkyviewCamera,
    e: MouseInputData,
    selected: Object3D | undefined,
  ): Vector3 | undefined {
    const rayCaster = camera.getRay(e.pointer.position.x, e.pointer.position.y);
    // How far the assets are picked from depends on how far the point is.
    const point =
      selected instanceof Geometry ? selected.getWorldPoints()[this.editPointIdx] : undefined;
    setRayCasterParamsBasedOnCamera(
      camera.getTHREECamera(),
      rayCaster,
      20,
      clamp((point ?? rayCaster.ray.origin).distanceTo(rayCaster.ray.origin), 0.1, 350),
    );

    const snapped = this.snapping.findSnapPoint(camera, rayCaster, {
      predicate: selected && excludeAsset(selected),
    });
    this.showCenterCurves(snapped.curves);
    return snapped.point;
  }

  /**
   * Highlights the curves whose center the point snaps to, in world coordinates, although the tool
   * is placed at the selected asset.
   */
  private showCenterCurves(curves: Conic[] | undefined) {
    this.centerHighlight.position.copy(this.position).negate();
    this.centerHighlight.show(curves);
  }

  onMouseOut(e: MouseInputData) {
    this.clearTooltip();
    this.ifcHoverPosition = undefined;
    this.hoverIfcObject(undefined);
    return true;
  }

  /**
   * Finds the object of an IFC model below the mouse once a frame, not at every move of the mouse,
   * as a model can have many objects.
   */
  private scheduleIfcHover(x: number, y: number) {
    const scheduled = isDefined(this.ifcHoverPosition);
    this.ifcHoverPosition = { x, y };

    if (!scheduled) {
      requestAnimationFrame(() => {
        const position = this.ifcHoverPosition;
        this.ifcHoverPosition = undefined;
        const camera = cameraManager().getActiveCamera();

        // Not after another tool has been chosen, a point has started to be moved or the flag has
        // been turned off since the mouse moved. The tool isn't visible after a click beside the
        // assets, but still in use.
        if (
          position &&
          camera &&
          this.inputHandler.currentTool() === this &&
          this.state === SelectToolState.Selecting &&
          this.ifcHoverHighlight
        ) {
          // Only the objects of the selected model, which would be distracting in all models.
          const selected = AssetManager.instance.getSelected();
          const assets = getIfcAssets(selected ? [selected] : []);
          this.hoverIfcObject(pickIfcObject(assets, camera, position.x, position.y));
        }
      });
    }
  }

  /**
   * Highlights the object of the selected IFC model that the mouse is over, as when its layer is
   * hovered in the layer list, where it is shown as hovered, or none.
   */
  hoverIfcObject(hovered: IfcPick | undefined) {
    const last = this.hoveredIfc;
    this.hoveredIfc = hovered;

    // Unless the layer list has hovered another layer since.
    if (last && last.asset !== hovered?.asset && last.asset.hoveredLayer === last.node) {
      last.asset.hoverLayer(undefined);
    }

    hovered?.asset.hoverLayer(hovered.node);
  }

  enable() {
    this.inputHandler.setCursor('');
    this.visible = true;
    this.state = SelectToolState.Selecting;
    this.editPointIdx = -1;

    const selected = AssetManager.instance.getSelected();
    if (selected) {
      this.position.copy(selected.position);
    }
  }

  disable() {
    this.assetMeasuring = false;
    this.showCenterCurves(undefined);
    this.heightsGuide.hide();
    this.inputHandler.setCursor('');
    this.visible = false;
    this.position.z = 4000;

    this.clearTooltip();
    this.clearContextMenu();
    this.ifcHoverPosition = undefined;
    this.hoverIfcObject(undefined);

    AssetManager.instance.select(undefined);
  }

  isEditing() {
    return this.state === SelectToolState.Editing;
  }

  updateTooltip() {
    if (!this.lastValidPoint) {
      return;
    }

    const selected = AssetManager.instance.getSelected();
    if (!selected) {
      return;
    }

    const camera = cameraManager().getActiveCamera();
    if (!camera) {
      return;
    }

    const screenPoint = camera.getScreenPosition(this.lastValidPoint);

    showToolTooltip(this, {
      ...buildTooltipProps(
        selected,
        camera,
        {
          easting: this.xCoord,
          northing: this.yCoord,
          height: this.zCoord,
        },
        {
          x: screenPoint.x,
          y: screenPoint.y,
        },
      ),
      heights: this.heightsGuide.getTooltipHeights(this.lastValidPoint),
    });
  }

  updateContextMenu(e: MouseInputData) {
    if (!this.mousePosition || e.pointer.client.distanceTo(this.mousePosition) > 10) {
      return false;
    }

    const items: ContextMenuItem[] = [];
    const selectedAsset = AssetManager.instance.getSelected();
    const isSharedView = ThemeManager.instance.isSharedView;
    const camera = cameraManager().getActiveCamera();

    // When the right-click landed on an editable vertex of a selected Geometry, show
    // "delete point" at the top of the menu.
    if (
      camera &&
      selectedAsset instanceof Geometry &&
      !isSharedView &&
      selectedAsset.showContextMenu(e.pointer.position.x, e.pointer.position.y, camera)
    ) {
      items.push({
        title: t('contextMenu.selectTool.deletePoint', { ns: 'skyview' }),
        action: async () => {
          try {
            const pointIndex = selectedAsset.getPointIndex(
              e.pointer.position.x,
              e.pointer.position.y,
              camera,
            );
            if (pointIndex >= 0) {
              selectedAsset.removePoint(pointIndex);
              await selectedAsset.onChange();
            }
          } finally {
            this.contextMenuCallback?.();
          }
        },
      });
    }

    const asset =
      AssetManager.instance.pick(
        e.pointer.position.x,
        e.pointer.position.y,
        camera?.getTHREECamera()!,
      ) ?? selectedAsset;

    // The object of an IFC model that was clicked, which is inspected and hidden as in its layers.
    const ifcNode =
      camera && asset instanceof Glb
        ? pickIfcObject(getIfcAssets([asset]), camera, e.pointer.position.x, e.pointer.position.y)
            ?.node
        : undefined;

    if (asset instanceof Glb && ifcNode) {
      items.push(
        {
          title: t('contextMenu.selectTool.inspect', { ns: 'skyview' }),
          // Without selecting the asset: its layers are opened by the inspection.
          action: () => {
            asset.inspectLayer(ifcNode);
            this.contextMenuCallback?.();
          },
        },
        {
          title: t('contextMenu.selectTool.hideIfcLayer', { ns: 'skyview' }),
          action: () => {
            asset.hideLayer(ifcNode);
            this.contextMenuCallback?.();
          },
        },
      );
    }

    const hasEditAccessToAsset =
      !isSharedView &&
      (asset instanceof Design
        ? userStateAdapter.getSnapshot().hasDesignToolAccessForCurrentProject()
        : true);

    if (isDefined(asset)) {
      items.push(toggleAsset(asset, this.contextMenuCallback), {
        title: t('delete', { ns: 'common' }),
        action: () => {
          publish(SubscriptionTopic.ShowDeleteAssetDialog, {
            asset,
            position: e.pointer.client,
          });
          this.contextMenuCallback?.();
        },
        disabled: !hasEditAccessToAsset,
      });
    }

    if (isDefined(asset) && asset.getStateVisibility() && asset.loadingStatus === 'loaded') {
      if (asset instanceof Design) {
        items.push(editAsset(this, asset, this.contextMenuCallback));
      }

      items.push(
        {
          title: t('contextMenu.selectTool.move', { ns: 'skyview' }),
          action: () => {
            AssetManager.instance.select(asset);
            InputHandler.instance.setTool('transform.translate');
            this.contextMenuCallback?.();
          },
          disabled:
            !hasEditAccessToAsset ||
            asset.freeze.translate === true ||
            !permissionChecks.hasAccessToFeature('feature:viewer:toolbar:translate'),
        },
        {
          title: t('contextMenu.selectTool.rotate', { ns: 'skyview' }),
          action: () => {
            AssetManager.instance.select(asset);
            InputHandler.instance.setTool('transform.rotate');
            this.contextMenuCallback?.();
          },
          disabled:
            !hasEditAccessToAsset ||
            asset.freeze.rotateZ === true ||
            !permissionChecks.hasAccessToFeature('feature:viewer:toolbar:rotate'),
        },
      );

      if (canAssetBeTransformedWithReferencePointTool(asset)) {
        items.push({
          title: t('contextMenu.selectTool.scale', { ns: 'skyview' }),
          action: () => {
            AssetManager.instance.select(asset);
            InputHandler.instance.setTool('reference-point-transform');
            this.contextMenuCallback?.();
          },
          disabled:
            !hasEditAccessToAsset ||
            !permissionChecks.hasAccessToFeature('feature:viewer:toolbar:scale') ||
            !userStateAdapter.getSnapshot().hasDesignToolAccessForCurrentProject(),
        });
      }
    }

    if (isDefined(selectedAsset) && selectedAsset.uuid === asset?.uuid) {
      items.push(deselectAsset(this.contextMenuCallback));
    }

    this.contextMenuCallback?.({
      pos: {
        x: e.viewportPos?.clientX ?? 0,
        y: e.viewportPos?.clientY ?? 0,
      },
      items,
    });

    return items.length > 0;
  }

  clearTooltip() {
    showToolTooltip(this);
  }
  clearContextMenu() {
    this.contextMenuCallback?.();
  }
}

/** An object of an IFC model, with its asset. */
type IfcPick = { asset: Glb; node: Object3D };

/**
 * Gets the shown IFC models that have loaded, among the assets.
 */
const getIfcAssets = (assets: Object3D[]) =>
  assets.filter(
    (x): x is Glb =>
      x instanceof Glb && x.visible && x.getFileType() === 'Ifc' && isDefined(x.glbData),
  );

/**
 * Gets the closest shown object of the IFC models that is at the position on screen, see
 * getIfcNode.
 */
function pickIfcObject(
  assets: Glb[],
  camera: SkyviewCamera,
  x: number,
  y: number,
): IfcPick | undefined {
  if (assets.length === 0) {
    return undefined;
  }

  const rayCaster = camera.getRay(x, y);
  // The closest hit of each mesh, see raycastThroughBoundsTrees.
  rayCaster.firstHitOnly = true;
  const hits = assets
    .flatMap((asset) =>
      rayCaster.intersectObject(asset.glbData!, true).map((intersection) => ({
        asset,
        intersection,
      })),
    )
    .filter((hit) => isShownBelow(hit.intersection.object))
    .sort((a, b) => a.intersection.distance - b.intersection.distance);
  // The edges of an object are picked from further away from the mouse than its faces.
  const hit = hits.find((x) => x.intersection.object instanceof THREE.Mesh) ?? hits[0];
  const node = hit && getIfcNode(hit.intersection.object, hit.asset.glbData);

  return node && { asset: hit.asset, node };
}
